// Identidade visual do grupo (white label): título, cor e logos para tema claro e escuro.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Monitor, Moon, Sun, Upload } from "lucide-react";
import { type ChangeEvent, useEffect, useState } from "react";
import { Alert, Button, Card, cx, EmptyState, Field, Input, PageHeader, Select } from "../../components/ui";
import { api, type Branding, type ThemeMode } from "../../lib/api";
import { useAction, useTree } from "../../lib/hooks";
import { BrandMark } from "../../lib/session";

const TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
const MAX_KB = 512;

export function BrandingPage() {
  const { data: tree } = useTree();
  const groups = tree?.groups.filter((g) => g.canManage) ?? [];
  const [groupId, setGroupId] = useState<number | null>(null);
  const gid = groupId ?? groups[0]?.id ?? null;
  const group = groups.find((g) => g.id === gid);

  return (
    <>
      <PageHeader title="Identidade visual" description="Logo, título e cor que os usuários do grupo veem no login e no topo da plataforma." />
      {!groups.length ? (
        <EmptyState title="Nenhum grupo para configurar" />
      ) : (
        <>
          {groups.length > 1 && (
            <div className="mb-4 max-w-xs">
              <Select aria-label="Grupo" value={gid ?? ""} onChange={(e) => setGroupId(Number(e.target.value))}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {group && <BrandingEditor key={group.id} groupId={group.id} slug={group.slug} subdomain={group.subdomain} baseDomain={tree?.baseDomain ?? ""} />}
        </>
      )}
    </>
  );
}

function BrandingEditor({ groupId, slug, subdomain, baseDomain }: { groupId: number; slug: string; subdomain: string | null; baseDomain: string }) {
  const qc = useQueryClient();
  const { data: current, refetch } = useQuery({
    queryKey: ["branding", slug],
    queryFn: () => api.get<Branding>(`/api/branding?grupo=${encodeURIComponent(slug)}`),
  });
  const { run, busy, error } = useAction([["me"]]);
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("#0f5fa8");
  const [theme, setTheme] = useState<ThemeMode>("auto");
  const [saved, setSaved] = useState(false);
  const [stamp, setStamp] = useState(Date.now()); // força recarregar o logo após upload

  useEffect(() => {
    if (!current) return;
    setTitle(current.title);
    setColor(current.primaryColor ?? "#0f5fa8");
    setTheme(current.theme ?? "auto");
  }, [current]);

  async function save() {
    setSaved(false);
    if (await run(() => api.put(`/api/admin/groups/${groupId}/branding`, { title: title.trim() || null, primaryColor: color, theme }))) {
      setSaved(true);
      await refetch();
    }
  }

  async function upload(variant: "light" | "dark", e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ok = await run(async () => {
      if (!TYPES.includes(file.type)) throw new Error("use PNG, JPG, WebP ou SVG");
      if (file.size > MAX_KB * 1024) throw new Error(`o logo pode ter no máximo ${MAX_KB} KB`);
      const dataBase64 = await toBase64(file);
      await api.put(`/api/admin/groups/${groupId}/branding/logo/${variant}`, { contentType: file.type, dataBase64 });
    });
    if (ok) {
      setStamp(Date.now());
      await refetch();
      await qc.invalidateQueries({ queryKey: ["me"] });
    }
  }

  async function removeLogo(variant: "light" | "dark") {
    if (await run(() => api.put(`/api/admin/groups/${groupId}/branding/logo/${variant}`, null))) await refetch();
  }

  const preview: Branding = {
    groupId,
    title: title || "Sem título",
    primaryColor: color,
    logoLight: !!current?.logoLight,
    logoDark: !!current?.logoDark,
    theme,
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
      <Card className="space-y-5 p-5">
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Título" hint="Aparece ao lado do logo quando não há logo, e na aba do navegador.">
          {(id) => <Input id={id} value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <Field label="Cor principal" hint="Botões, links e destaques.">
          {(id) => (
            <div className="flex items-center gap-3">
              <input id={id} type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-14 cursor-pointer rounded border border-line bg-surface" />
              <Input value={color} onChange={(e) => setColor(e.target.value)} pattern="#[0-9a-fA-F]{6}" className="w-32 font-mono" aria-label="Cor em hexadecimal" />
            </div>
          )}
        </Field>
        <Field label="Tema de cores" hint="Vale para todas as telas do grupo, inclusive o login. Automático segue a configuração do navegador de cada usuário.">
          {(id) => (
            <div id={id} className="inline-flex rounded-md border border-line bg-surface p-0.5" role="radiogroup" aria-label="Tema de cores">
              {(
                [
                  ["auto", "Automático", Monitor],
                  ["light", "Claro", Sun],
                  ["dark", "Escuro", Moon],
                ] as const
              ).map(([value, label, Icon]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={theme === value}
                  onClick={() => setTheme(value)}
                  className={cx(
                    "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm",
                    theme === value ? "bg-brand text-white" : "text-muted hover:bg-surface-2 hover:text-fg",
                  )}
                >
                  <Icon className="size-4" aria-hidden /> {label}
                </button>
              ))}
            </div>
          )}
        </Field>
        <div className="flex items-center gap-3">
          <Button onClick={save} loading={busy}>
            Salvar
          </Button>
          {saved && <span className="text-sm text-ok">Salvo</span>}
        </div>

        <div className="grid gap-4 border-t border-line pt-5 sm:grid-cols-2">
          {(["light", "dark"] as const).map((v) => {
            const has = v === "light" ? current?.logoLight : current?.logoDark;
            return (
              <div key={v}>
                <p className="mb-2 text-sm font-medium">{v === "light" ? "Logo para fundo claro" : "Logo para fundo escuro"}</p>
                <div className={`flex h-24 items-center justify-center rounded-md border border-line p-3 ${v === "dark" ? "bg-[#171c24]" : "bg-white"}`}>
                  {has ? (
                    <img src={`/api/branding/${groupId}/logo/${v}?t=${stamp}`} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-xs text-[#8a93a3]">sem logo</span>
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-line bg-surface px-3 text-sm hover:bg-surface-2">
                    <Upload className="size-3.5" aria-hidden /> Enviar
                    <input type="file" accept={TYPES.join(",")} className="sr-only" onChange={(e) => upload(v, e)} />
                  </label>
                  {has && (
                    <Button size="sm" variant="ghost" onClick={() => removeLogo(v)}>
                      Remover
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted">PNG, JPG, WebP ou SVG, até {MAX_KB} KB. Prefira logo horizontal, com fundo transparente.</p>
      </Card>

      <div className="space-y-3">
        <p className="text-sm font-medium">Prévia</p>
        <Card className="p-4" >
          <div style={{ ["--brand" as string]: color }} className="space-y-4">
            <BrandMark branding={preview} />
            <Button>Entrar</Button>
          </div>
        </Card>
        <p className="text-xs text-muted">
          Endereço de login do grupo:{" "}
          <code className="font-mono">{subdomain && baseDomain ? `${subdomain}.${baseDomain}` : `${location.host}/login?grupo=${slug}`}</code>
        </p>
      </div>
    </div>
  );
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(new Error("não foi possível ler o arquivo"));
    r.readAsDataURL(file);
  });
}
